import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import type {
  DrinkEntry,
  DrinkStats,
  CreateDrinkEntryInput,
  UpdateDrinkEntryInput,
  DrinkEntryFilters,
} from '../model/types'

// Re-export types
export type { DrinkEntry, DrinkStats, CreateDrinkEntryInput, UpdateDrinkEntryInput }

/**
 * Параметры для получения записей чайного дневника
 */
export interface GetDrinkHistoryParams extends Omit<DrinkEntryFilters, 'userId'> {
  page?: number
  limit?: number
}

/**
 * Ответ API для списка записей
 */
export interface DrinkHistoryResponse {
  success: boolean
  data: DrinkEntry[]
}

/**
 * Ответ API для пагинированного списка записей
 */
export interface PaginatedDrinkHistoryResponse {
  success: boolean
  data: DrinkEntry[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

/**
 * Ответ API для одной записи
 */
export interface DrinkHistoryEntryResponse {
  success: boolean
  data: DrinkEntry
}

/**
 * Ответ API для статистики
 */
export interface DrinkStatsResponse {
  success: boolean
  data: DrinkStats
}

/**
 * Параметры заваривания
 */
export interface BrewingParams {
  temperature?: number
  time?: number
  teaAmount?: number
  waterAmount?: number
}

/**
 * Запрос на создание записи (упрощённый для UI)
 */
export interface CreateDrinkHistoryRequest {
  teaId?: string
  rating?: number
  mood?: string             // relaxed, focused, energized, happy
  brewingMethod?: string    // gongfu, western, grandpa
  brewingParams?: BrewingParams
  notes?: string
  location?: string
  servingCount?: number
  steepCount?: number
  drankAt?: string
  userPhotoUrl?: string
}

/**
 * Хук для получения записей чайного дневника
 * 
 * Использует TanStack Query для кэширования. Требует авторизации (initData).
 * Поддерживает фильтрацию по чаю, дате и настроению.
 * 
 * @param params - Параметры фильтрации и пагинации
 * @returns Query результат с данными записей
 * 
 * @example
 * ```tsx
 * // Все записи
 * const { data } = useDrinkHistory()
 * 
 * // Фильтр по чаю
 * const { data } = useDrinkHistory({ teaId: '123' })
 * ```
 */
export function useDrinkHistory(params: GetDrinkHistoryParams = {}) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['diary', params],
    queryFn: async () => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }

      const searchParams = new URLSearchParams()
      if (params.limit) searchParams.append('limit', String(params.limit))
      if (params.page !== undefined) searchParams.append('offset', String(params.page * (params.limit || 20)))
      if (params.teaId) searchParams.append('teaId', params.teaId)
      if (params.fromDate) searchParams.append('fromDate', params.fromDate)
      if (params.toDate) searchParams.append('toDate', params.toDate)
      if (params.mood) searchParams.append('mood', params.mood)

      const queryString = searchParams.toString()
      const endpoint = `/api/user/diary${queryString ? `?${queryString}` : ''}`

      // Если есть limit, используем пагинированный ответ
      if (params.limit) {
        const response = await apiClient<PaginatedDrinkHistoryResponse>(endpoint, { initData })
        return { success: response.success, data: response.data }
      }

      return apiClient<DrinkHistoryResponse>(endpoint, { initData })
    },
    enabled: !!initData,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Хук для получения записей с бесконечной прокруткой (infinite scroll)
 * 
 * @param params - Параметры фильтрации
 * @param pageSize - Размер страницы (по умолчанию 20)
 */
export function useInfiniteDrinkHistory(
  params: Omit<GetDrinkHistoryParams, 'page' | 'limit'> = {},
  pageSize = 20
) {
  const { initData } = useTelegram()

  return useInfiniteQuery({
    queryKey: ['diary-infinite', params, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }

      const searchParams = new URLSearchParams()
      searchParams.append('limit', String(pageSize))
      searchParams.append('offset', String(pageParam))
      if (params.teaId) searchParams.append('teaId', params.teaId)
      if (params.fromDate) searchParams.append('fromDate', params.fromDate)
      if (params.toDate) searchParams.append('toDate', params.toDate)
      if (params.mood) searchParams.append('mood', params.mood)

      const endpoint = `/api/user/diary?${searchParams.toString()}`

      return apiClient<PaginatedDrinkHistoryResponse>(endpoint, { initData })
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined
      return lastPage.offset + lastPage.limit
    },
    enabled: !!initData,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Хук для получения статистики дневника
 */
export function useDrinkStats() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['diary-stats'],
    queryFn: async () => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }
      return apiClient<DrinkStatsResponse>('/api/user/diary/stats', { initData })
    },
    enabled: !!initData,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Хук для получения последних записей
 */
export function useRecentDrinkEntries(limit: number = 5) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['diary-recent', limit],
    queryFn: async () => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }
      return apiClient<DrinkHistoryResponse>(`/api/user/diary/recent?limit=${limit}`, { initData })
    },
    enabled: !!initData,
    staleTime: 2 * 60 * 1000,
  })
}

/**
 * Хук для создания записи в чайном дневнике
 * 
 * Использует TanStack Query Mutation. После успешного создания автоматически
 * инвалидирует кэш записей дневника.
 * 
 * @returns Mutation объект с методами mutate, mutateAsync и состоянием
 * 
 * @example
 * ```tsx
 * const createEntry = useCreateDrinkHistoryEntry()
 * 
 * const handleSubmit = () => {
 *   createEntry.mutate({
 *     teaId: '123',
 *     teaName: 'Дянь Хун',
 *     rating: 5,
 *     notes: 'Отличный чай',
 *     mood: 'relaxed',
 *   })
 * }
 * ```
 */
export function useCreateDrinkHistoryEntry() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateDrinkHistoryRequest) => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }

      return apiClient<DrinkHistoryEntryResponse>('/api/user/diary', {
        method: 'POST',
        body: JSON.stringify(data),
        initData,
      })
    },
    onSuccess: () => {
      // Инвалидируем все связанные кэши дневника
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['diary-infinite'] })
      queryClient.invalidateQueries({ queryKey: ['diary-stats'] })
      queryClient.invalidateQueries({ queryKey: ['diary-recent'] })
    },
  })
}

/**
 * Запрос на обновление записи
 */
export interface UpdateDrinkHistoryRequest extends UpdateDrinkEntryInput {
  entryId: string
}

/**
 * Хук для обновления записи в чайном дневнике
 */
export function useUpdateDrinkHistoryEntry() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateDrinkHistoryRequest) => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }

      const { entryId, ...updateData } = data

      return apiClient<DrinkHistoryEntryResponse>(`/api/user/diary/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(updateData),
        initData,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['diary-infinite'] })
      queryClient.invalidateQueries({ queryKey: ['diary-stats'] })
      queryClient.invalidateQueries({ queryKey: ['diary-recent'] })
    },
  })
}

/**
 * Хук для удаления записи из чайного дневника
 */
export function useDeleteDrinkHistoryEntry() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (entryId: string) => {
      if (!initData) {
        throw new Error('Требуется авторизация')
      }

      return apiClient(`/api/user/diary/${entryId}`, {
        method: 'DELETE',
        initData,
      })
    },
    // Optimistic update
    onMutate: async (entryId) => {
      await queryClient.cancelQueries({ queryKey: ['diary'] })
      
      const previousData = queryClient.getQueryData<DrinkHistoryResponse>(['diary', {}])
      
      if (previousData) {
        const updatedEntries = previousData.data.filter(entry => entry.id !== entryId)
        queryClient.setQueryData<DrinkHistoryResponse>(['diary', {}], {
          ...previousData,
          data: updatedEntries,
        })
      }
      
      return { previousData }
    },
    onError: (_err, _entryId, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['diary', {}], context.previousData)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['diary'] })
      queryClient.invalidateQueries({ queryKey: ['diary-infinite'] })
      queryClient.invalidateQueries({ queryKey: ['diary-stats'] })
      queryClient.invalidateQueries({ queryKey: ['diary-recent'] })
    },
  })
}

